import React, { useEffect, useRef, useState } from 'react';

const Reviews = (props) => {
  const reviewsContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    // Initialize AOS if available
    if (typeof window !== 'undefined' && window.AOS) {
      window.AOS.init({
        duration: 800,
        delay: 100,
        once: true,
        offset: 50
      });
    }
  }, []);

  useEffect(() => {
    loadReviewCarousel();
    
    return () => {
      // Cleanup function
      const existingScript = document.querySelector('script[src*="widget/carousel"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const loadReviewCarousel = () => {
    try {
      setIsLoading(true);
      setHasError(false);
      
      // Check if script is already loaded
      if (document.querySelector('script[src*="widget/carousel"]')) {
        setScriptLoaded(true);
        setIsLoading(false);
        return;
      }
      
      // Create the carousel container if it doesn't exist
      if (reviewsContainerRef.current) {
        const carouselDiv = document.createElement('div');
        carouselDiv.id = 'review-carousel';
        carouselDiv.style.width = '100%';
        carouselDiv.style.minHeight = '400px';
        reviewsContainerRef.current.appendChild(carouselDiv);
      }
      
      // Load the Review Harvest carousel script
      const script = document.createElement('script');
      script.src = 'https://widget.reviewharvest.com/widget/carousel?business-id=5260f06a-7bed-47d0-8e49-bd469d3aa22a&tag=carousel&star-color=%23FFC107&button-color=%231567f1&button-text-color=%23ffffff&card-background-color=%23FFFFFF&text-color=%23333333&auto-slide-interval=5000&show-reviewer=true';
      script.defer = true;
      
      script.onload = () => {
        console.log('Review Harvest carousel script loaded successfully');
        setScriptLoaded(true);
        setIsLoading(false);
        setHasError(false);
      };
      
      script.onerror = () => {
        console.error('Failed to load Review Harvest carousel script');
        setScriptLoaded(false);
        setIsLoading(false);
        setHasError(true);
      };
      
      document.head.appendChild(script);
      
      // Set timeout to handle loading issues
      setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
          // Check if the carousel div has content
          const carouselDiv = document.getElementById('review-carousel');
          if (carouselDiv && carouselDiv.innerHTML.trim() === '') {
            setHasError(true);
          }
        }
      }, 8000);
      
    } catch (error) {
      console.error('Error loading Review Harvest carousel:', error);
      setIsLoading(false);
      setHasError(true);
    }
  };

  const retryLoading = () => {
    // Remove existing script and container
    const existingScript = document.querySelector('script[src*="widget/carousel"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const existingCarousel = document.getElementById('review-carousel');
    if (existingCarousel) {
      existingCarousel.remove();
    }
    
    // Clear the container
    if (reviewsContainerRef.current) {
      reviewsContainerRef.current.innerHTML = '';
    }
    
    // Reload
    loadReviewCarousel();
  };

  const renderLoadingState = () => (
    <div className="text-center py-5">
      <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
        <span className="sr-only">Loading...</span>
      </div>
      <h4>Loading customer reviews...</h4>
      <p className="text-muted">Please wait while we load our review carousel.</p>
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center py-5">
      <div className="alert alert-warning">
        <i className="fas fa-exclamation-triangle mb-3" style={{ fontSize: '2rem' }}></i>
        <h4>Unable to Load Reviews</h4>
        <p>We are having trouble loading the review carousel. Please try again or refresh the page.</p>
        <div className="mt-3">
          <button 
            className="btn btn-primary mr-2"
            onClick={retryLoading}
          >
            <i className="fas fa-redo mr-2"></i>
            Try Again
          </button>
          <button 
            className="btn btn-outline-primary"
            onClick={() => window.location.reload()}
          >
            <i className="fas fa-refresh mr-2"></i>
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );

  const renderFallbackReviews = () => (
    <div className="fallback-reviews-info">
      <div className="alert alert-info">
        <i className="fas fa-info-circle mr-2"></i>
        <strong>Reviews Loading Issue:</strong> The review carousel is temporarily unavailable. 
        You can view our latest reviews on <a href="https://www.google.com/search?q=BlueBox+Moving+reviews" target="_blank" rel="noopener noreferrer">Google</a>.
      </div>
      
      <div className="row">
        <div className="col-md-4 text-center">
          <div className="p-4 bg-light rounded">
            <h3 className="text-primary">4.9/5</h3>
            <p className="mb-0">Average Rating</p>
          </div>
        </div>
        <div className="col-md-4 text-center">
          <div className="p-4 bg-light rounded">
            <h3 className="text-primary">156+</h3>
            <p className="mb-0">Customer Reviews</p>
          </div>
        </div>
        <div className="col-md-4 text-center">
          <div className="p-4 bg-light rounded">
            <h3 className="text-primary">95%</h3>
            <p className="mb-0">Satisfaction Rate</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="reviews-page">
      {/* Header Section */}
      <div className="container-fluid bg-primary text-white py-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="display-4 mb-3" data-aos="fade-up">
              Reviews
              </h1>
              <p className="lead" data-aos="fade-up" data-aos-delay="100">
                See what our customers say about our services
              </p>
              <div className="mt-4" data-aos="fade-up" data-aos-delay="200">
                <span className="badge badge-light badge-pill px-3 py-2 mr-2 mb-2">
                  <i className="fas fa-star text-warning"></i> 5/5 Rating
                </span>
                <span className="badge badge-light badge-pill px-3 py-2 mr-2 mb-2">
                  <i className="fas fa-users text-primary"></i> 156+ Reviews
                </span>
                <span className="badge badge-light badge-pill px-3 py-2 mb-2">
                  <i className="fab fa-google text-success"></i> Google Reviews
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Carousel Container */}
      <div className="container py-5">
        <div className="row">
          <div className="col-12">
            <div className="reviews-carousel-container">
              {isLoading && renderLoadingState()}
              
              {hasError && (
                <>
                  {renderErrorState()}
                  {renderFallbackReviews()}
                </>
              )}
              
              <div 
                ref={reviewsContainerRef}
                className="review-carousel-wrapper"
                style={{ 
                  display: isLoading ? 'none' : 'block',
                  minHeight: '400px',
                  width: '100%'
                }}
              >
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose BlueBox Section */}
      <div className="container-fluid bg-light py-5">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <h3 className="mb-4" data-aos="fade-up">
                Why Choose BlueBox?
              </h3>
              <ul className="list-unstyled" data-aos="fade-up" data-aos-delay="100">
                <li className="mb-3">
                  <i className="fas fa-check-circle text-success mr-2"></i>
                  <strong>Sustainable Moving Solutions</strong> - Eco-friendly box rental service
                </li>
                <li className="mb-3">
                  <i className="fas fa-check-circle text-success mr-2"></i>
                  <strong>Free Delivery & Pickup</strong> - Convenient door-to-door service
                </li>
                <li className="mb-3">
                  <i className="fas fa-check-circle text-success mr-2"></i>
                  <strong>Professional Service</strong> - Trusted by 500+ customers
                </li>
                <li className="mb-3">
                  <i className="fas fa-check-circle text-success mr-2"></i>
                  <strong>Flexible Rental Periods</strong> - Rent for as long as you need
                </li>
              </ul>
            </div>
            <div className="col-md-6">
              <h3 className="mb-4" data-aos="fade-up">
                Service Statistics
              </h3>
              <div className="row">
                <div className="col-6 text-center mb-3">
                  <div className="stat-card p-3 bg-white rounded shadow-sm">
                    <h4 className="text-primary mb-1">4.9/5</h4>
                    <small className="text-muted">Average Rating</small>
                  </div>
                </div>
                <div className="col-6 text-center mb-3">
                  <div className="stat-card p-3 bg-white rounded shadow-sm">
                    <h4 className="text-primary mb-1">156+</h4>
                    <small className="text-muted">Reviews</small>
                  </div>
                </div>
                <div className="col-6 text-center mb-3">
                  <div className="stat-card p-3 bg-white rounded shadow-sm">
                    <h4 className="text-primary mb-1">95%</h4>
                    <small className="text-muted">Satisfaction</small>
                  </div>
                </div>
                <div className="col-6 text-center mb-3">
                  <div className="stat-card p-3 bg-white rounded shadow-sm">
                    <h4 className="text-primary mb-1">500+</h4>
                    <small className="text-muted">Customers</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reviews;